import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateChild, Router } from '@angular/router';
import { ApplicationContextService } from '../services/application-context.service';
import { map } from 'rxjs';
import { AuthenticationService } from '../services/auth.service';
import {
  hasMultipleVisibleCompanies,
  resolveAdminOrganizationContext,
} from '../utils/admin-company.util';
import { AdminScopeService } from '../services/admin-scope.service';

/** Paths allowed while Super Admin is in "All organizations" scope. */
function isAllOrgsQueuePath(url: string): boolean {
  const path = (url || '').split('?')[0];
  return (
    path === '/professional-profiles' ||
    path.startsWith('/professional-profiles/') ||
    path === '/practice-requests' ||
    path.startsWith('/practice-requests/') ||
    path === '/provider-profiles' ||
    path.startsWith('/provider-profiles/')
  );
}

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate, CanActivateChild {
    constructor(private readonly _authService: AuthenticationService,
        private readonly _router: Router,
        private readonly _applicationContextService: ApplicationContextService,
        private readonly _adminScope: AdminScopeService) {

    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this._applicationContextService.context.pipe(map(context => {
            const user = context.currentlyLoggedInUser;
            const userId = user?.id;
            if (!userId) {
                this._authService.clearSessionAndRedirectToLogin();
                return false;
            }

            // Only Super Global Admin may use All-orgs scope.
            if (!user.superGlobalAdmin && this._adminScope.isAllOrganizations) {
                this._adminScope.setScope('company');
            }

            const onSelectCompany = state.url.startsWith('/select-company');
            const allOrgs =
              !!user.superGlobalAdmin && this._adminScope.isAllOrganizations;
            const needsOrg =
              resolveAdminOrganizationContext({
                companyId: user.companyId,
                userCompanies: context.userCompanies,
                superGlobalAdmin: !!user.superGlobalAdmin,
                allOrganizationsScope: allOrgs,
              }) === 'select-company';

            if (!onSelectCompany && needsOrg) {
              const reason = hasMultipleVisibleCompanies(context.userCompanies)
                ? 'multiple-orgs'
                : 'no-org-context';
              void this._router.navigateByUrl(`/select-company?reason=${reason}`);
              return false;
            }

            // All-orgs only supports cross-company queues — bounce company pages.
            if (allOrgs && !onSelectCompany && !isAllOrgsQueuePath(state.url)) {
              void this._router.navigateByUrl('/provider-profiles');
              return false;
            }

            return true;
        }))
    }

    canActivateChild(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot) {
        return this.canActivate(next, state);
    }
}
